<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard id="campaign-personalization-edit" class="mb-8" :loading="savingCampaignPersonalization">
    <template #header>
      <ContentCardSubheader @back="onCancel">
        {{ t('Reviews.product.campaignPersonalisation.edit.title', { productName: product.title }) }}
      </ContentCardSubheader>
    </template>
    <div v-if="loading" class="mt-6 flex justify-center">
      <CircleSpinner />
    </div>
    <div v-else-if="campaign && personalization" class="py-6">
      <div class="grid grid-cols-2 gap-6 desktop:grid-cols-5">
        <div class="flex flex-col gap-y-3 desktop:col-span-2">
          <div ref="inviteMessageHintRef" class="txt-body-m text-main">
            <MarkdownContent
              :unstyled="true"
              :md="
                t('Reviews.product.campaign.form.inviteMessage.edit.hint', {
                  language: t(personalization.locale),
                  link: inviteMessageHintLink,
                }).toString()
              "
            />
          </div>
          <div>
            <VuelidateField :validation="v$.inviteMessage">
              <template #default="{ error, state, countHint }">
                <FieldWrapper
                  for="campaign-inviteMessage-edit"
                  :count-hint="countHint"
                  :hint="t('Reviews.product.campaign.form.inviteMessage.input.hint', { min: TEXT_MIN_LENGTH, max: TEXT_MAX_LENGTH })"
                  :error="error"
                >
                  <template #default="{ id }">
                    <TextareaField :id="id" v-model="v$.inviteMessage.$model" rows="8" :state="state" />
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
          <button class="btn-outline-neutral-xs w-fit" data-testid="reviews-pers-edit-reset" @click="resetInviteMessage">
            <Undo2Icon class="lucide-icon-s" />
            {{ t('Reviews.product.action.resetInviteMessage') }}
          </button>
        </div>
        <div class="desktop:col-span-3">
          <PersonalizationPreviewBox :invite-message="v$.inviteMessage.$model" />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-row items-center justify-end gap-x-3">
        <button class="btn-outline-color-s" data-testid="reviews-pers-edit-cancel" @click="onCancel">
          {{ t('Reviews.product.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="reviews-pers-edit-save" @click="onSubmit">
          {{ t('Reviews.product.action.saveChanges') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>

<script setup lang="ts">
import { CircleSpinner, FieldWrapper, TextareaField, MarkdownContent } from '@ramp106/omrjs-core-ui'
import { Undo2Icon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import PersonalizationPreviewBox from '@/components/ReviewsManagement/Reviews/PersonalizationPreviewBox.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs.ts'
import { useMarkdownLinkNavigation } from '@/composables/useMarkdownLinkNavigation'
import {
  useReviewsCampaignPersonalizationFormOptions,
  TEXT_MIN_LENGTH,
  TEXT_MAX_LENGTH,
} from '@/composables/useReviewsCampaignPersonalizationFormOptions'
import {
  IncentivizationCampaignPersonalizationLocaleEnum,
  useReviewsIncentivizationCampaignQuery,
  useUpdateReviewsCampaignPersonalizationMutation,
  type ProductData,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import { maxLength, minLength, required } from '@/services/validators'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

import type { CampaignFormData } from './types'

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const confirmCloseModal = useTemplateRef('confirmCloseModal')

const campaignId = route.params.campaignId as string
const personalizationId = route.params.personalizationId as string

const { result, loading } = useReviewsIncentivizationCampaignQuery(
  computed(() => ({ campaignId })),
  { clientId: 'reviews' },
)

const campaign = computed(() => result.value?.manage?.incentivizationCampaign)

const campaignTitle = computed(() => campaign.value?.title || t('Reviews.product.view.campaignDetailView.loading'))
const breadcrumbs = useReviewsProductBreadcrumbs(props.product, campaignTitle)

const personalization = computed(() => {
  if (!campaign.value?.personalizations) {
    return null
  }
  return campaign.value.personalizations.find((p) => p.id === personalizationId) || null
})

const inviteMessageHintLink = computed(
  () => router.resolve({ name: ReviewsRouteName.ReviewsProductCampaignPersonalizationNew, params: { campaignId: campaignId } }).path,
)

const inviteMessageHintRef = useTemplateRef<HTMLElement>('inviteMessageHintRef')

useMarkdownLinkNavigation(inviteMessageHintRef, inviteMessageHintLink)

watch([loading, campaign], ([isLoading, campaignData]) => {
  if (!isLoading && !campaignData) {
    router.push({ name: '404' })
  }
  if (!isLoading && campaignData && !personalization.value) {
    router.push({ name: '404' })
  }
})

const detailsFormData = computed<Pick<CampaignFormData, 'incentivizationType' | 'incentive' | 'amount'>>(() => {
  if (!campaign.value) {
    return {
      incentive: false,
      incentivizationType: undefined,
      amount: undefined,
    }
  }
  return {
    incentive: !!campaign.value.incentivizationsLimit,
    incentivizationType: campaign.value.incentivizationType,
    amount: campaign.value.amount,
  }
})

const localeRef = computed(() => {
  return personalization.value?.locale || IncentivizationCampaignPersonalizationLocaleEnum.De
})

const { getDefaultInviteMessage } = useReviewsCampaignPersonalizationFormOptions(
  detailsFormData,
  computed(() => props.product),
  localeRef,
)

const formData = reactive({
  inviteMessage: personalization.value?.inviteMessage || '',
})

watch(
  () => personalization.value,
  (newPersonalization) => {
    if (newPersonalization) {
      formData.inviteMessage = newPersonalization.inviteMessage || ''
    }
  },
  { immediate: true },
)

const rules = computed(() => ({
  inviteMessage: {
    required,
    minLength: minLength(TEXT_MIN_LENGTH),
    maxLength: maxLength(TEXT_MAX_LENGTH),
  },
}))

const $externalResults = ref<Partial<Record<'inviteMessage', string>>>({})

const v$ = useVuelidate(rules, formData, { $externalResults })

const { mutate: updateCampaignPersonalization, loading: savingCampaignPersonalization } = useUpdateReviewsCampaignPersonalizationMutation({
  clientId: 'reviews',
})

const backTo = computed(() => ({
  name: ReviewsRouteName.ReviewsProductCampaignOverview,
  params: { campaignId: campaignId },
}))

function resetInviteMessage() {
  v$.value.inviteMessage.$model = getDefaultInviteMessage()
  if ($externalResults.value.inviteMessage) {
    delete $externalResults.value.inviteMessage
  }
}

function onCancel() {
  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo.value)
  }
}

async function onSubmit() {
  if (v$.value.$externalResults) {
    v$.value.$clearExternalResults()
  }

  if (v$.value.$invalid) {
    v$.value.$touch()
    return
  }

  if (!personalizationId) {
    return
  }

  try {
    const response = await updateCampaignPersonalization({
      campaignPersonalization: personalizationId,
      attributes: {
        inviteMessage: formData.inviteMessage,
      },
    })

    if (
      response?.data?.manage.updateCampaignPersonalization.errors &&
      response.data.manage.updateCampaignPersonalization.errors.length > 0
    ) {
      const errors: Partial<Record<'inviteMessage', string>> = {}
      response.data.manage.updateCampaignPersonalization.errors.forEach((error) => {
        if (error.field === 'inviteMessage') {
          errors.inviteMessage = error.message
        }
      })
      $externalResults.value = errors
      showNotification(t('Reviews.product.campaignPersonalisation.edit.genericErrorMessage'), 'error')
    } else {
      showNotification(t('Reviews.product.campaignPersonalisation.edit.successMessage'), 'success')
      router.push(backTo.value)
    }
  } catch (_error) {
    showNotification(t('Reviews.product.campaignPersonalisation.edit.genericErrorMessage'), 'error')
  }
}
</script>
